/**
 * @file Controlled Drugs export report printing.
 */

import { appData } from '../../app/state/state.js';
import { sanitizeHTML } from '../../shared/utils/contentUtils.js';
import { openPrintWindow, showPrintError } from '../../shared/reporting/printWindow.js';

/**
 * Prints the Controlled Drugs export report returned by the export callable.
 * @param {Array<object>} data Exported transaction rows.
 * @param {object} meta Export metadata and resolved filters.
 * @returns {void}
 */
export function printReport(data, meta) {
    if (!data || data.length === 0) {
        showPrintError("No data to print.");
        return;
    }

    // Open in a new tab (passing empty string for features)
    const reportWindow = openPrintWindow();
    if (!reportWindow) {return;}

    // Correct metadata access: meta IS already the reportMeta object
    const usedTimeZone = meta.timezone || 'Pacific/Auckland';
    const reportNum = meta.reportNumber || 'N/A';
    const title = "Controlled Drugs Report";
    const safe = (value) => sanitizeHTML(String(value ?? ""));

    // Helper for 24h Time - Strictly using local browser time for "Generated At"
    const format24hLocal = (dateInput) => {
        if (!dateInput) {return '';}
        const d = (typeof dateInput === 'string') ? new Date(dateInput) : dateInput;
        if (isNaN(d.getTime())) {return dateInput;}

        const pad = (n) => String(n).padStart(2, '0');
        const day = pad(d.getDate());
        const month = pad(d.getMonth() + 1);
        const year = d.getFullYear();
        const hours = pad(d.getHours());
        const minutes = pad(d.getMinutes());

        return `${day}/${month}/${year} ${hours}:${minutes}`;
    };

    const generatedDate = format24hLocal(new Date());
    const user = appData.users.find(u => u.uid === meta.generatedBy)?.name || 'Unknown User';

    // Helper for Title Case
    const toTitleCase = (str) => {
        if (!str) {return '';}
        const clean = str.replace(/_/g, ' ');
        return clean.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    };

    // --- Format Filters for Display (2-Column Grid) ---
    const f = meta.filters || {};
    const rf = meta.resolvedFilters || {};

    const filterPairs = [
        { label: 'Dates', value: `${f.startDate || "All Time"} to ${f.endDate || "Present"}` },
        { label: 'Types', value: rf.types || 'All' },
        { label: 'Drugs', value: rf.drugs || 'All' },
        { label: 'Locations', value: rf.locations || 'All' },
        { label: 'Performed By', value: rf.performers || 'All' },
        { label: 'Approved By', value: rf.approvers || 'All' },
        { label: 'Hide Inactive', value: f.hideInactive ? 'Yes' : 'No' },
        { label: 'Include Audits', value: f.includeAudits ? 'Yes' : 'No' }
    ];

    const filterHtml = `
        <div class="filter-grid">
            ${filterPairs.map(p => `
                <div class="filter-item">
                    <strong>${safe(p.label)}:</strong> <span>${safe(p.value)}</span>
                </div>
            `).join('')}
        </div>
    `;

    // Generate Table Rows
    const rows = data.map(row => {
        let drugDisplayName = row.drug;
        if (appData.drugInventory) {
            const di = appData.drugInventory.find(d => d.name === row.drug);
            if (di) {drugDisplayName = di.shortName || di.name;}
        }

        const formatFullTimestamp = (s) => {
            if (!s) {return '';}
            const sStr = String(s);
            // If the backend already sends a formatted string "DD/MM/YYYY HH:mm", use it directly
            if (sStr.includes('/') && sStr.includes(':')) {
                return sStr;
            }
            // Fallback for raw ISO or unexpected formats
            const d = new Date(s);
            if (isNaN(d.getTime())) {return sStr;}
            const pad = (n) => String(n).padStart(2, '0');
            return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
        };

        // Custom status mapping
        let displayStatus = row.status || '';
        if (displayStatus === 'automatic_approval') {displayStatus = 'Automatic';}
        else {displayStatus = toTitleCase(displayStatus);}

        // Format Quantity with Breakdown
        let qtyDisplay = row.quantity || '';
        if (row.type === 'use' && (row.administered !== undefined || row.discarded !== undefined)) {
            const admin = row.administered !== undefined ? row.administered : 0;
            const disc = row.discarded !== undefined ? row.discarded : 0;
            // Rounding happens in backend or use original values, assuming values are numbers
            // Use small grey text for the breakdown
            qtyDisplay = `${safe(qtyDisplay)}<div style="font-size: 7pt; color: #666; font-weight: normal;">(${safe(admin)}/${safe(disc)})</div>`;
        } else {
            qtyDisplay = safe(qtyDisplay);
        }

        return `
            <tr>
                <td class="id-cell">${safe(row.transactionId?.slice(-6) || '')}</td>
                <td style="white-space: nowrap; font-weight: 600;">${safe(row.date || '-')}</td>
                <td style="font-size: 7.5pt;">${safe(toTitleCase(row.type))}</td>
                <td class="font-bold">${safe(drugDisplayName)}</td>
                <td style="font-size: 7.5pt;">${safe(row.location)}</td>
                <td class="qty-cell font-bold">${qtyDisplay}</td>
                <td class="qty-cell font-bold">${safe(row.balanceAfter || '')}</td>
                <td style="font-size: 7.5pt;">${safe(row.patient || '')}</td>
                <td style="font-size: 7.5pt;">${safe(row.provider || '')}</td>
                <td>
                    <div class="user-name">${safe(row.performedBy)}</div>
                    <div class="timestamp">${safe(formatFullTimestamp(row.performedAt))}</div>
                </td>
                <td>
                    <div class="user-name">${safe(row.approvedBy || '-')}</div>
                    <div class="timestamp">${safe(formatFullTimestamp(row.approvedAt || row.voidedAt))}</div>
                </td>
                <td style="font-size: 7.5pt;">${safe(displayStatus)}</td>
                <td class="notes-cell">${safe(row.notes || '-')}</td>
            </tr>
        `;
    }).join('');

    reportWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
            <title>${safe(title)}</title>
            <style>
                @page { size: A4 landscape; margin: 8mm; }
                body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; font-size: 8.5pt; color: #111; line-height: 1.1; margin: 0; }
                h1 { font-size: 13pt; margin: 0 0 4pt 0; color: #000; letter-spacing: -0.01em; }
                .header { margin-bottom: 10pt; border-bottom: 1.5pt solid #000; padding-bottom: 4pt; }
                .meta { font-size: 7.5pt; color: #444; margin-bottom: 6pt; display: flex; justify-content: space-between; align-items: flex-end; }

                .filter-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 2pt 15pt;
                    background: #f5f5f5;
                    padding: 5pt 8pt;
                    border: 0.5pt solid #ccc;
                    border-radius: 2pt;
                    font-size: 7.5pt;
                    line-height: 1.2;
                }
                .filter-item { display: flex; gap: 4pt; }
                .filter-item strong { min-width: 70pt; color: #111; }
                .filter-item span { color: #555; overflow: hidden; text-overflow: ellipsis; }

                table { width: 100%; border-collapse: collapse; table-layout: fixed; margin-top: 8pt; }
                th { text-align: left; background-color: #e5e5e5; padding: 3pt 2pt; border: 0.5pt solid #999; font-weight: bold; font-size: 7.5pt; text-transform: uppercase; color: #333; }
                td { padding: 3pt 2pt; border: 0.5pt solid #ddd; vertical-align: top; word-wrap: break-word; font-size: 8pt; }
                tr:nth-child(even) { background-color: #fafafa; }

                .font-bold { font-weight: 700; color: #000; }
                .qty-cell { font-size: 8.5pt; color: #000; }
                .id-cell { font-size: 7.5pt; color: #888; font-family: monospace; }

                .user-name { font-weight: 600; font-size: 8pt; }
                .timestamp { font-size: 7.5pt; color: #666; font-weight: 500; margin-top: 1pt; }

                .notes-cell { font-size: 7.5pt; color: #444; line-height: 1.1; }

                @media print {
                    body { -webkit-print-color-adjust: exact; }
                    .no-print { display: none; }
                }
            </style>
        </head>
        <body>
            <div class="header">
                <h1>${safe(title)}</h1>
                <div class="meta">
                    <div>
                        <strong>DRUG REPORT # ${safe(reportNum)}</strong> &bull; Records: ${safe(data.length)}
                    </div>
                    <div style="text-align: right;">
                        Generated: ${safe(generatedDate)} (${safe(usedTimeZone)}) &bull; By: ${safe(user)}
                    </div>
                </div>
                ${filterHtml}
            </div>

            <table>
                <thead>
                    <tr>
                        <th style="width: 35pt;">ID Tail</th>
                        <th style="width: 50pt;">Date</th>
                        <th style="width: 55pt;">Type</th>
                        <th style="width: 90pt;">Drug</th>
                        <th style="width: 60pt;">Location</th>
                        <th style="width: 25pt;">Qty</th>
                        <th style="width: 25pt;">Bal</th>
                        <th style="width: 80pt;">Patient</th>
                        <th style="width: 75pt;">Provider</th>
                        <th style="width: 75pt;">Performed</th>
                        <th style="width: 75pt;">Approved</th>
                        <th style="width: 52pt;">Status</th>
                        <th style="width: 130pt;">Notes & Justifications</th>
                    </tr>
                </thead>
                <tbody>
                    ${rows}
                </tbody>
            </table>

            <div style="margin-top: 20pt; font-size: 7.5pt; text-align: center; color: #999; border-top: 0.5pt solid #eee; padding-top: 4pt;">
                Controlled Drug Register Extraction &bull; Forensic Audit Trail (24h Time - ${safe(usedTimeZone)}) &bull; End of Report
            </div>

            <script>
                // window.onload = function() {
                //     window.print();
                //     window.close();
                // }
            </script>
        </body>
        </html>
    `);

    reportWindow.document.close();
}
